import t from "@/lang/lang";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import { FieldErrors } from "@/shared/components/field/field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { TextInputField } from "@/shared/components/field/input-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { DocumentBaseDialog } from "@/services/backend/documents/components/document-base-dialog";
import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { RepresentativeCard } from "@/services/backend/representatives/components/representative-card";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import {
  PatchRequest,
  updateOmniOfferCache,
  useOmniOfferPatchMutation,
  useOmniOfferResetReferenceNumberMutation,
} from "../api/offer-api";
import { Offer, Patch, patchOffer } from "../types/offer";
import { offerTextVariables } from "../types/offer-text-variables";
import { ServiceObjectReferencesBaseCard } from "@/services/backend/documents/components/service-object-references-base-card";
import { ServiceObjectsSelect } from "@/services/backend/customers/components/service-objects-select";
import { updateServiceObjectsByIds } from "@/services/backend/documents/types/service-object-references-base";
import { useResetRemovedServiceObjectFields } from "@/services/backend/documents/lib/use-reset-removed-service-object-fields";

export function OfferDataPage({ offer }: { offer: Offer }) {
  const request: PatchRequest = { id: offer.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateOmniOfferCache,
    useOmniOfferPatchMutation(),
  );
  const patch = (p: Patch) => set({ ...request, ...p }, patchOffer(offer, p));
  const disabled = isImmutable(offer);

  return (
    <div className="grid gap-2">
      <OfferCard offer={offer} onPatch={patch} disabled={disabled} />
      <div className="grid gap-2 xl:grid-cols-2">
        <CustomerAddressCard
          customerAddress={offer.customerAddress}
          onCustomerAddressChange={(customerAddress) =>
            patch({ customerAddress })
          }
          description={t(
            "Adresse, an welche das Angebot geschickt werden soll.",
          )}
          fieldName="customerAddress"
          disabled={disabled}
        />
        <ServiceObjectReferencesBaseCard
          references={offer}
          onReferencesChange={(refs) => patch(refs)}
          className="row-span-2"
          disabled={disabled}
        />
        <RepresentativeCard
          representative={offer.representative}
          onRepresentativeChange={(representative) => patch({ representative })}
          fieldName="representative"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function OfferCard({
  offer,
  onPatch,
  disabled,
}: {
  offer: Offer;
  onPatch: (patch: Patch) => void;
  disabled: boolean;
}) {
  const patchReferenceNumber = useOmniOfferPatchMutation();
  const resetReferenceNumber = useOmniOfferResetReferenceNumberMutation();
  const { errors: customerErrors } = useFieldErrors("customerId");
  const resetRemovedServiceObjectFields = useResetRemovedServiceObjectFields();

  const variables = offerTextVariables(offer);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Angebot")}</CardTitle>
        <CardDescription>
          {t(
            "Kerndaten des Angebots. Erweiterte Informationen können über das Info-Icon aufgerufen werden.",
          )}
        </CardDescription>
        <CardAction>
          <DocumentBaseDialog
            document={offer}
            onPatch={onPatch}
            disabled={disabled}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-4">
        <ReferenceNumberField
          data={offer}
          updateCache={updateOmniOfferCache}
          patchMutation={patchReferenceNumber}
          resetMutation={resetReferenceNumber}
          disabled={disabled}
        />
        <DatePickerField
          label={t("Angebotsdatum")}
          nullable={true}
          date={offer.date}
          onDateChange={(date) => onPatch({ date: date ?? null })}
          fieldName="date"
          disabled={disabled}
        />
        <Field>
          <FieldLabel>{t("Kunde")}</FieldLabel>
          <CustomerSelect value={offer.customerId} disabled />
          <FieldErrors errors={customerErrors} />
        </Field>
        <Field>
          <FieldLabel>{t("Serviceobjekt")}</FieldLabel>
          <ServiceObjectsSelect
            customerId={offer.customerId}
            value={offer.serviceObjects.map((s) => s.serviceObjectId)}
            onValueChange={(ids) => {
              resetRemovedServiceObjectFields(offer, ids);
              onPatch(updateServiceObjectsByIds(offer, ids));
            }}
            disabled={disabled}
          />
          <FieldErrors errors={customerErrors} />
        </Field>
        <TextInputField
          label={t("Titel")}
          text={offer.title}
          onTextChange={(title) => onPatch({ title })}
          fieldName="title"
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
        <TextInputField
          label={t("Untertitel")}
          text={offer.subtitle}
          onTextChange={(subtitle) => onPatch({ subtitle })}
          fieldName="subtitle"
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
        <EditorInputField
          // The editor highlights the variables it is mounted with.
          key={variables.map((v) => v.name).join(",")}
          label={t("Text")}
          initialHtml={offer.text}
          onHtmlChange={(text) => onPatch({ text })}
          variables={variables}
          fieldName="text"
          fieldProps={{ className: "lg:col-span-4" }}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}
